import React from 'react';

/**
 * Reusable time slot selector with 15-minute intervals
 * Used for: Availability schedules, time selection
 * Using native select to avoid Radix ref issues with many items
 */
const TimeSlotSelector = ({ value, onChange, placeholder = "Select time", disabled = false, className = '', minTime = null }) => {
  // Generate time slots in 15-minute intervals from 00:00 to 23:45
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = String(hour).padStart(2, '0');
        const minStr = String(minute).padStart(2, '0');
        const time = `${hourStr}:${minStr}`;
        
        // Skip times before minTime if provided
        if (minTime) {
          const [minHour, minMin] = minTime.split(':').map(Number);
          const currentMinutes = hour * 60 + minute;
          const minMinutes = minHour * 60 + minMin;
          
          if (currentMinutes <= minMinutes) {
            continue; // Skip this time slot
          }
        }
        
        // Format for display (12-hour)
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const display = `${displayHour}:${minStr} ${ampm}`;
        
        slots.push({ value: time, label: display });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();
  
  // Normalize the incoming value to 24-hour format (HH:mm)
  // Handles: "9:00 AM", "09:00", "9:00", "14:30", "2:30 PM", etc.
  const normalizeTimeValue = (val) => {
    if (!val) return '';
    
    // If already in HH:mm format, return as-is
    if (/^\d{2}:\d{2}$/.test(val)) return val;
    
    // Parse 12-hour format like "9:00 AM" or "2:30 PM"
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      const period = match[3]?.toUpperCase();
      
      if (period === 'PM' && hour < 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      
      return `${String(hour).padStart(2, '0')}:${minute}`;
    }
    
    return val;
  };

  const normalizedValue = normalizeTimeValue(value);

  return (
    <select 
      value={normalizedValue} 
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`w-full h-11 px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 ${className}`}
    >
      <option value="" disabled>{placeholder}</option>
      {timeSlots.map((slot) => (
        <option key={slot.value} value={slot.value}>
          {slot.label}
        </option>
      ))}
    </select>
  );
};

export default TimeSlotSelector;
